import React, { Fragment, useEffect } from 'react';
import Head from 'next/head';
import { isEmpty, upperFirst } from 'lodash';
import { useSelector } from 'react-redux';
import { b2cPagesService } from '@clubmed/utils';
import { addTranslationKeys, t } from 'Src/i18n';
import { SITEPLAN_PAGE_ID } from 'Routes/constants';
import { STATIC_PAGE_TYPES, VIRTUAL_PAGE_LOAD } from 'Tracking/constants';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { PAGE_SITEPLAN } from 'Utils/request/requestDictionary';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Title } from 'Components/frameworks/title/Title';
import { Hero } from 'Components/widgets/hero/Hero';
import { userInfoSelector } from 'Store/user-info/userInfo.selectors';
const { PAGE_TYPES } = b2cPagesService;
const { DESTINATION, LANDING, RESORT, TOUR, OFFER } = PAGE_TYPES;
type SiteplanViewProps = {
  pages: Array<{
    pageId: string;
    pageType: string;
    title: string;
    url: string;
  }>;
};
type SiteplanProps = SiteplanViewProps & {
  config: {
    siteplan: {
      destinationSectionTitle: string;
      landingSectionTitle: string;
      offerSectionTitle: string;
      pageImmersiveTitle: string;
      pageImmersiveUrl: string;
      pageTitle: string;
      resortSectionTitle: string;
      tourSectionTitle: string;
    };
  };
};
export const SiteplanView = (props: SiteplanViewProps) => {
  const { isDesktop } = useResponsive();
  const trackPageView = useTracking();
  const profile: UserInfo = useSelector(userInfoSelector);
  const SITE_SECTIONS = [
    {
      pageType: DESTINATION,
      title: t('siteplan.destinationSectionTitle'),
    },
    {
      pageType: LANDING,
      title: t('siteplan.landingSectionTitle'),
    },
    {
      pageType: OFFER,
      title: t('siteplan.offerSectionTitle'),
    },
    {
      pageType: RESORT,
      title: t('siteplan.resortSectionTitle'),
    },
    {
      pageType: TOUR,
      title: t('siteplan.tourSectionTitle'),
    },
  ];
  useEffect(() => {
    const pageName = `Shopping_${upperFirst(SITEPLAN_PAGE_ID)}`;
    trackPageView({
      categoryGA: SITEPLAN_PAGE_ID,
      event: VIRTUAL_PAGE_LOAD,
      pageName,
      fidelityStatus: profile?.loyaltyProgram?.status || '',
    });
  }, []);
  const { pages = [] } = props;
  const siteSections = SITE_SECTIONS.map((section) => ({
    ...section,
    pages: pages.filter((page) => page.pageType === section.pageType),
  })).filter((section) => !isEmpty(section.pages));
  return (
    <Fragment>
      <Head>
        <title>{t('siteplan.pageTitle')}</title>
      </Head>
      <header className="Line Line-Immersive" role="banner">
        <Hero
          title={t('siteplan.pageImmersiveTitle')}
          size="small"
          url={t('siteplan.pageImmersiveUrl')}
        />
      </header>
      <main className="Line" role="main">
        <SiteContainer>
          {siteSections.map(({ title: sectionTitle, pages: sectionPages }) => (
            <Fragment key={sectionTitle}>
              <Title className="mb-20 sm:mb-15" hLevel={3} theme="title-sans-n4">
                {sectionTitle}
              </Title>
              <ul
                className="list-none p-0 text-12 lg:text-14"
                style={
                  isDesktop
                    ? {
                        columnCount: 2,
                      }
                    : {}
                }
              >
                {sectionPages.map((page) => (
                  <li key={page.pageId}>
                    <DynamicLink url={page.url}>{page.title || page.pageId}</DynamicLink>
                  </li>
                ))}
              </ul>
            </Fragment>
          ))}
        </SiteContainer>
      </main>
    </Fragment>
  );
};

SiteplanView.getCMInitialProps = ({
  appContext,
}: GetInitialPropsContext): Promise<SiteplanViewProps> => {
  const pageTypes = [DESTINATION, LANDING, RESORT, TOUR, OFFER]
    .map((pageType) => `"${pageType}"`)
    .join(',');
  const query = `query {
    config {
      siteplan {
        destinationSectionTitle
        landingSectionTitle
        offerSectionTitle
        pageImmersiveTitle
        pageImmersiveUrl
        pageTitle
        resortSectionTitle
        tourSectionTitle
      }
    }
    pages: pagesByTypes(pageTypes: [${pageTypes}]) {
      pageId
      pageType
      title
      url
    }
  }`;
  return graphQLClient
    .runQuery(query, appContext, {
      requestName: PAGE_SITEPLAN,
    })
    .then(({ config, pages = [] }: SiteplanProps) => {
      addTranslationKeys({
        siteplan: config.siteplan,
      });
      return {
        pages,
      };
    });
};

SiteplanView.pageId = SITEPLAN_PAGE_ID;
SiteplanView.pageType = STATIC_PAGE_TYPES.SHOPPING_SITEPLAN;
